/**
 * The whole "UI framework" of this editor: element construction, nothing else. Panels rebuild
 * their subtree from state instead of diffing, so these helpers only have to make that rebuild
 * short to write. Keep this file tiny — every panel depends on it.
 */

export type Child = Node | string | null | undefined | false;
/** `class`/`text` are shorthands; `onclick`-style keys become listeners; `true` sets a flag. */
export type Attrs = Record<string, unknown>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (value === true) node.setAttribute(key, '');
    else node.setAttribute(key, String(value));
  }
  append(node, children);
  return node;
}

export function append(parent: HTMLElement, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
}

/**
 * The icon table: real Lucide icons (https://lucide.dev/icons/), inlined so the app ships no icon
 * font, no dependency and no network request. Design rule 5 ("no text-only buttons") is what this
 * table exists for.
 *
 * Pinned to **lucide-static v1.33.0**. Each value is the verbatim *inside* of that version's
 * `icons/<name>.svg` — drawing elements only (`path`, `circle`, `rect`, `line`), with nothing but
 * the newlines and indentation removed. Lucide's ROOT attributes (`fill="none"`,
 * `stroke="currentColor"`, `stroke-width="2"`, linecap/linejoin) are deliberately NOT copied:
 * `icon()` builds the `<svg class="i">` and `svg.i` in `panels.css` supplies them, at our lighter
 * `stroke-width: 1.6`. `fill`/`stroke` are inherited properties, so the `<rect>`s in
 * `film`/`copy`/`pause` stroke like everything else instead of filling.
 *
 * The Lucide name is the kebab-case of the key unless a trailing comment names a different one.
 * `logo` is ours — a hand-drawn brand mark, not a Lucide icon; leave it alone when refreshing.
 *
 * To update: bump the version above, then refetch every entry from
 * `https://unpkg.com/lucide-static@<version>/icons/<lucide-name>.svg` (or the matching tag under
 * `https://raw.githubusercontent.com/lucide-icons/lucide/`), keeping the markup verbatim.
 *
 * @license Lucide icons — ISC. Full text: https://unpkg.com/lucide-static@1.33.0/LICENSE
 *
 *   ISC License
 *
 *   Copyright (c) 2026 Lucide Icons and Contributors
 *
 *   Permission to use, copy, modify, and/or distribute this software for any
 *   purpose with or without fee is hereby granted, provided that the above
 *   copyright notice and this permission notice appear in all copies.
 *
 *   THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 *   WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 *   MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 *   ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 *   WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 *   ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 *   OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * A subset of these (check, chevron-*, crosshair, download, info, plus, trash-2, upload, x, …) is
 * derived from Feather and additionally carries Feather's MIT notice — see the LICENSE above.
 */
export const ICONS = {
  logo: '<path d="M12 3v18M12 8l-6 3M12 8l6 3"/><circle cx="12" cy="3" r="1.6"/><circle cx="6" cy="11" r="1.6"/><circle cx="18" cy="11" r="1.6"/><circle cx="12" cy="21" r="1.6"/>',
  chevronDown: '<path d="m6 9 6 6 6-6"/>',
  chevronRight: '<path d="m9 18 6-6-6-6"/>',
  undo: '<path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"/>', // undo-2
  redo: '<path d="m15 14 5-5-5-5"/><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13"/>', // redo-2
  download: '<path d="M12 15V3"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/>',
  upload: '<path d="M12 3v12"/><path d="m17 8-5-5-5 5"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>',
  save: '<path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/>',
  box: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
  bone: '<path d="M17 10c.7-.7 1.69 0 2.5 0a2.5 2.5 0 1 0 0-5 .5.5 0 0 1-.5-.5 2.5 2.5 0 1 0-5 0c0 .81.7 1.8 0 2.5l-7 7c-.7.7-1.69 0-2.5 0a2.5 2.5 0 0 0 0 5c.28 0 .5.22.5.5a2.5 2.5 0 1 0 5 0c0-.81-.7-1.8 0-2.5Z"/>',
  map: '<path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z"/><path d="M15 5.764v15"/><path d="M9 3.236v15"/>',
  film: '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 3v18"/><path d="M3 7.5h4"/><path d="M3 12h18"/><path d="M3 16.5h4"/><path d="M17 3v18"/><path d="M17 7.5h4"/><path d="M17 16.5h4"/>',
  layers: '<path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"/><path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"/><path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"/>',
  // The two outliner glyphs (`hierarchyPanel` draws them at 12px for root / every other bone).
  // Plain `circle` fills the whole box and `dot` is a 1px speck at that size; these two read as
  // joints — a ring with a centre origin, and a smaller plain ring.
  joint: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="1"/>', // circle-dot
  jointSmall: '<circle cx="12" cy="12" r="6"/>', // circle-small
  expand: '<path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M14 15H9v-5"/><path d="M16 3h5v5"/><path d="M21 3 9 15"/>', // scaling
  frameAll: '<path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>', // scan
  reset: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>', // rotate-ccw
  mirror: '<path d="m3 7 5 5-5 5V7"/><path d="m21 7-5 5 5 5V7"/><path d="M12 20v2"/><path d="M12 14v2"/><path d="M12 8v2"/><path d="M12 2v2"/>', // flip-horizontal-2
  zap: '<path d="M15.914 4a1.5 1.5 0 00-2.474-1.561l-9 9A1.5 1.5 0 005.5 14h4.002a.5.5 0 01.471.666L8.086 20a1.5 1.5 0 002.475 1.56l9-9A1.5 1.5 0 0018.5 10h-3.997a.5.5 0 01-.472-.667z"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  alert: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>', // triangle-alert
  sliders: '<path d="M10 5H3"/><path d="M12 19H3"/><path d="M14 3v4"/><path d="M16 17v4"/><path d="M21 12h-9"/><path d="M21 19h-5"/><path d="M21 5h-7"/><path d="M8 10v4"/><path d="M8 12H3"/>', // sliders-horizontal
  target: '<circle cx="12" cy="12" r="10"/><line x1="22" x2="18" y1="12" y2="12"/><line x1="6" x2="2" y1="12" y2="12"/><line x1="12" x2="12" y1="6" y2="2"/><line x1="12" x2="12" y1="22" y2="18"/>', // crosshair
  plus: '<path d="M5 12h14"/><path d="M12 5v14"/>',
  trash: '<path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>', // trash-2
  copy: '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  pencil: '<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/>',
  refresh: '<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/>', // refresh-cw
  folder: '<path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>',
  eye: '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/>',
  key: '<path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0Z"/>', // diamond
  play: '<path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"/>',
  pause: '<rect x="14" y="3" width="5" height="18" rx="1"/><rect x="5" y="3" width="5" height="18" rx="1"/>',
  skipBack: '<path d="M17.971 4.285A2 2 0 0 1 21 6v12a2 2 0 0 1-3.029 1.715l-9.997-5.998a2 2 0 0 1-.003-3.432z"/><path d="M3 20V4"/>',
  skipForward: '<path d="M21 4v16"/><path d="M6.029 4.285A2 2 0 0 0 3 6v12a2 2 0 0 0 3.029 1.715l9.997-5.998a2 2 0 0 0 .003-3.432z"/>',
  stepBack: '<path d="M13.971 4.285A2 2 0 0 1 17 6v12a2 2 0 0 1-3.029 1.715l-9.997-5.998a2 2 0 0 1-.003-3.432z"/><path d="M21 20V4"/>',
  stepForward: '<path d="M10.029 4.285A2 2 0 0 0 7 6v12a2 2 0 0 0 3.029 1.715l9.997-5.998a2 2 0 0 0 .003-3.432z"/><path d="M3 4v16"/>',
  camera: '<path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z"/><circle cx="12" cy="13" r="3"/>',
  flag: '<path d="M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>', // circle-help
  more: '<circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>', // ellipsis
  loop: '<path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>', // repeat
  magnet: '<path d="m12 15 4 4"/><path d="M2.352 10.648a1.205 1.205 0 0 0 0 1.704l2.296 2.296a1.205 1.205 0 0 0 1.704 0l6.029-6.029a1 1 0 1 1 3 3l-6.029 6.029a1.205 1.205 0 0 0 0 1.704l2.296 2.296a1.205 1.205 0 0 0 1.704 0l6.365-6.367A1 1 0 0 0 8.716 4.282z"/><path d="m5 8 4 4"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  open: '<path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"/>', // folder-open
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  chevronUp: '<path d="m18 15-6-6-6 6"/>',
} as const;

export type IconName = keyof typeof ICONS;

/**
 * A 14px stroke icon. SVG contributes nothing to `textContent`, so adding one to a button never
 * changes the label the tests match on (`design/ui-design.md` §6).
 */
export function icon(name: IconName, size = 14): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'i');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = ICONS[name];
  return svg;
}

/**
 * `label` may be a plain string or a child list, so an action can carry its icon
 * (`button([icon('save'), 'Save'], …)`) without the icon leaking into the accessible name.
 */
export function button(
  label: string | Child[],
  onClick: () => void,
  attrs: Attrs = {},
): HTMLButtonElement {
  return el(
    'button',
    { type: 'button', ...attrs, onclick: onClick },
    typeof label === 'string' ? [label] : label,
  );
}

/**
 * Merge a required class with whatever the caller passed. Spreading `attrs` last would silently
 * drop the class the component is defined by (`.iconly`, `.toggle`, `.pill`).
 */
function withClass(base: string, attrs: Attrs): Attrs {
  const extra = attrs.class;
  return { ...attrs, class: extra ? `${base} ${String(extra)}` : base };
}

/** An icon-only action. `title` doubles as the accessible name — there is no visible label. */
export function iconButton(
  name: IconName,
  title: string,
  onClick: () => void,
  attrs: Attrs = {},
): HTMLButtonElement {
  return button([icon(name)], onClick, {
    ...withClass('iconly', attrs),
    title,
    'aria-label': title,
  });
}

/**
 * A file picker that looks like a button: the native input is hidden inside the `<label>`, so a
 * click anywhere on the label opens the picker without any script.
 *
 * `attrs` land on the INPUT (that is what tests drive), and the input is reset after every pick so
 * choosing the SAME file twice still fires `change`.
 */
export function fileButton(
  label: string,
  accept: string,
  onFile: (file: File) => void,
  attrs: Attrs = {},
): HTMLLabelElement {
  const input = el('input', { type: 'file', accept, hidden: true, ...attrs });
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) onFile(file);
    input.value = '';
  });
  return el('label', { class: 'filebtn' }, [icon('upload'), label, input]);
}

/** A boolean slot: same label/value geometry as `field()`, but it never scrubs. */
export function switchField(
  label: string,
  checked: boolean,
  onChange: (value: boolean) => void,
  attrs: Attrs = {},
): HTMLElement {
  const box = el('input', { type: 'checkbox', ...withClass('toggle', attrs) });
  box.checked = checked;
  box.addEventListener('change', () => onChange(box.checked));
  return el('div', { class: 'field switch' }, [el('span', { class: 'flabel', text: label }), box]);
}

/** A round toggle button. Pressed state is `aria-pressed` + `.active` (blue = UI state, rule 2). */
export function pill(
  label: string,
  active: boolean,
  onClick: () => void,
  attrs: Attrs = {},
): HTMLButtonElement {
  return button(label, onClick, {
    ...withClass(active ? 'pill active' : 'pill', attrs),
    'aria-pressed': active ? 'true' : 'false',
  });
}

export interface Option { value: string; label: string }

export function select(
  options: readonly Option[],
  value: string,
  onChange: (value: string) => void,
  attrs: Attrs = {},
): HTMLSelectElement {
  const node = el('select', attrs);
  for (const option of options) {
    const item = document.createElement('option');
    item.value = option.value;
    item.textContent = option.label;
    node.appendChild(item);
  }
  // Assign after the options exist, otherwise the browser drops an unknown value.
  node.value = value;
  node.addEventListener('change', () => onChange(node.value));
  return node;
}

/** Commits on `change` (not `input`) so a rebuild never interrupts typing. */
export function numberInput(
  value: number,
  onChange: (value: number) => void,
  attrs: Attrs = {},
): HTMLInputElement {
  const node = el('input', { type: 'number', step: '0.01', ...attrs });
  node.value = String(value);
  node.addEventListener('change', () => {
    const parsed = Number(node.value);
    if (Number.isFinite(parsed)) onChange(parsed);
    else node.value = String(value);
  });
  return node;
}

export function textInput(
  value: string,
  onChange: (value: string) => void,
  attrs: Attrs = {},
): HTMLInputElement {
  const node = el('input', { type: 'text', ...attrs });
  node.value = value;
  node.addEventListener('change', () => onChange(node.value));
  return node;
}

export function checkbox(
  label: string,
  checked: boolean,
  onChange: (checked: boolean) => void,
  attrs: Attrs = {},
): HTMLLabelElement {
  const box = el('input', { type: 'checkbox', ...attrs });
  box.checked = checked;
  box.addEventListener('change', () => onChange(box.checked));
  return el('label', { class: 'check' }, [box, ' ', label]);
}
